// VRG-640 — react-query hooks for /internal/analytics's website section.
import { useQuery } from '@tanstack/react-query'

import { websiteAnalyticsApi } from 'services'
import type {
  DayRange,
  VitalName,
  WebsiteJourneyQuery,
  WebsiteSessionFilters,
} from 'services'

/**
 * Five-minute staleTime matches the API's own cache (WEBSITE_ANALYTICS_CACHE_TTL_MS):
 * a tab switch or a re-render should not fire a request the server would answer from
 * cache anyway. `retry: false` — a 403 is a settled answer, not a transient failure.
 */
const FIVE_MINUTES = 5 * 60 * 1000

export const useWebsiteOverview = (range: DayRange, enabled = true) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'overview', range.since, range.until, range.from ?? null],
    queryFn: () => websiteAnalyticsApi.overview(range),
    staleTime: FIVE_MINUTES,
    retry: false,
    // VRG-712: only the Overview tab reads it; the other tabs do not wait on Vercel.
    enabled,
  })

/** VRG-690 — the Page analysis table (Vercel per path + PostHog time / entries / exits). */
export const useWebsitePages = (range: DayRange, enabled = true) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'pages', range.since, range.until, range.from ?? null],
    queryFn: () => websiteAnalyticsApi.pages(range),
    staleTime: FIVE_MINUTES,
    retry: false,
    // VRG-695: the SEO page reads this too, but only for a viewer holding Analytics.
    enabled,
  })

/**
 * VRG-762 — Core Web Vitals per route and per device (Vercel Speed Insights).
 *
 * `trend` is in the key because the API fetches the daily series for ONE vital: switching
 * the charted vital is a new query, not a client-side reslice of data it does not have.
 */
export const useWebsiteVitals = (range: DayRange, trend: VitalName, enabled = true) =>
  useQuery({
    queryKey: [
      'internal',
      'website-analytics',
      'vitals',
      range.since,
      range.until,
      range.from ?? null,
      trend,
    ],
    queryFn: () => websiteAnalyticsApi.vitals(range, trend),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
    // Switching the charted vital keeps the table up rather than blanking it for a moment:
    // the p75 columns are identical across trend choices, only the series changes.
    placeholderData: (previous) => previous,
  })

/** VRG-734 — GTM → Content marketing → Analytics (PostHog, per /insights piece). */
export const useWebsiteContent = (range: DayRange, enabled = true) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'content', range.since, range.until],
    queryFn: () => websiteAnalyticsApi.content(range),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

/** VRG-690 — the site-flow Sankey (PostHog page sequences). */
export const useWebsiteFlows = (range: DayRange) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'flows', range.since, range.until, range.from ?? null],
    queryFn: () => websiteAnalyticsApi.flows(range),
    staleTime: FIVE_MINUTES,
    retry: false,
  })

/** VRG-714 — the funnel Sankey (PostHog per-visitor paths, the waitlist joined server-side). */
export const useWebsiteFunnel = (range: DayRange, enabled = true) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'funnel', range.since, range.until, range.from ?? null],
    queryFn: () => websiteAnalyticsApi.funnel(range),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })

/** Live now polls every 30s (the API caches it for 30s too). */
export const useWebsiteLive = () =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'live'],
    queryFn: () => websiteAnalyticsApi.live(),
    refetchInterval: 30 * 1000,
    // VRG-689: the live map polls modestly and stops while the tab is hidden.
    refetchIntervalInBackground: false,
    staleTime: 30 * 1000,
    retry: false,
  })

/** Admin-only on the server. `enabled` keeps a non-admin from even asking. */
export const useWebsiteSessions = (filters: WebsiteSessionFilters, enabled: boolean) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'sessions', filters],
    queryFn: () => websiteAnalyticsApi.sessions(filters),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
    // VRG-712: turning a page keeps the current rows up (the grid shows its loader over them)
    // rather than blanking the table between pages.
    placeholderData: (previous) => previous,
  })

/** VRG-712 — one visitor's journey (admin-only on the server). Off until a visitor is picked. */
export const useWebsiteJourney = (q: WebsiteJourneyQuery | null) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'journey', q],
    queryFn: () => websiteAnalyticsApi.journey(q as WebsiteJourneyQuery),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled: !!q && !!(q.personId || q.sessionId),
  })

export const useWebsiteLeads = (enabled: boolean) =>
  useQuery({
    queryKey: ['internal', 'website-analytics', 'leads'],
    queryFn: () => websiteAnalyticsApi.leads(),
    staleTime: FIVE_MINUTES,
    retry: false,
    enabled,
  })
